// Comprehensive Supabase Service Layer for VA Sprouts
// Handles all backend API integration with proper error handling and RLS compliance

import supabase from '../utils/supabase.js';
import { escapePostgresLike } from '../utils/sqlSecurity.js';
import emailService from '../utils/emailService.js';

// ============================================================================
// AUTHENTICATION SERVICES
// ============================================================================

export const authService = {
  // Get current user session
  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return { user, error: null };
    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, error: error.message };
    }
  },

  // Get user profile with role information
  getUserProfile: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      return { profile: data, error: null };
    } catch (error) {
      console.error('Get user profile error:', error);
      return { profile: null, error: error.message };
    }
  },

  // Update user profile
  updateUserProfile: async (userId, updates) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', userId)
        .select()
        .single();
      
      if (error) throw error;
      return { profile: data, error: null };
    } catch (error) {
      console.error('Update user profile error:', error);
      return { profile: null, error: error.message };
    }
  }
};

// ============================================================================
// JOB MANAGEMENT SERVICES
// ============================================================================

export const jobService = {
  // Create new job posting
  createJob: async (jobData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('jobs')
        .insert({
          ...jobData,
          employer_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return { job: data, error: null };
    } catch (error) {
      console.error('Create job error:', error);
      return { job: null, error: error.message };
    }
  },

  // Get jobs for employer dashboard
  getEmployerJobs: async (employerId, filters = {}) => {
    try {
      let query = supabase
        .from('jobs')
        .select(`
          *,
          job_applications(count)
        `)
        .eq('employer_id', employerId)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.job_type) {
        query = query.eq('job_type', filters.job_type);
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return { jobs: data, error: null };
    } catch (error) {
      console.error('Get employer jobs error:', error);
      return { jobs: [], error: error.message };
    }
  },

  // Get active jobs for candidate search
  getActiveJobs: async (filters = {}) => {
    try {
      let query = supabase
        .from('jobs')
        .select(`
          *,
          user_profiles!jobs_employer_id_fkey(company_name, profile_picture)
        `)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      // Apply search filters
      if (filters.search) {
        // Security: Escape special characters to prevent ILIKE pattern injection
        const escapedSearch = escapePostgresLike(filters.search);
        query = query.or(`title.ilike.%${escapedSearch}%,description.ilike.%${escapedSearch}%`);
      }
      if (filters.location) {
        query = query.eq('location', filters.location);
      }
      if (filters.job_type) {
        query = query.eq('job_type', filters.job_type);
      }
      if (filters.experience_level) {
        query = query.eq('experience_level', filters.experience_level);
      }
      if (filters.skills && filters.skills.length > 0) {
        // Use RPC to search jobs by skills in a single query (eliminates N+1 per-skill loops)
        const validSkills = filters.skills.filter(s => s && s.trim()).map(s => s.trim());
        if (validSkills.length > 0) {
          try {
            const { data: matchingJobIds, error: rpcError } = await supabase
              .rpc('search_jobs_by_skills', { skill_array: validSkills });

            if (rpcError) throw rpcError;

            if (matchingJobIds && matchingJobIds.length > 0) {
              query = query.in('id', matchingJobIds);
            } else {
              query = query.eq('id', '00000000-0000-0000-0000-000000000000');
            }
          } catch (error) {
            console.error('Skills filter error:', error);
            // Continue without skills filter on error
          }
        }
      }
      if (filters.min_salary) {
        query = query.gte('budget_min', filters.min_salary);
      }
      if (filters.max_salary) {
        query = query.lte('budget_max', filters.max_salary);
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return { jobs: data, error: null };
    } catch (error) {
      console.error('Get active jobs error:', error);
      return { jobs: [], error: error.message };
    }
  },

  // Update job posting
  updateJob: async (jobId, updates) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', jobId)
        .select()
        .single();
      
      if (error) throw error;
      return { job: data, error: null };
    } catch (error) {
      console.error('Update job error:', error);
      return { job: null, error: error.message };
    }
  },

  // Delete job posting
  deleteJob: async (jobId) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId);
      
      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Delete job error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get job by ID with applications
  getJobById: async (jobId) => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          user_profiles!jobs_employer_id_fkey(company_name, profile_picture, email),
          job_applications(
            *,
            user_profiles!job_applications_applicant_id_fkey(full_name, profile_picture, skills, hourly_rate)
          )
        `)
        .eq('id', jobId)
        .single();
      
      if (error) throw error;
      return { job: data, error: null };
    } catch (error) {
      console.error('Get job by ID error:', error);
      return { job: null, error: error.message };
    }
  }
};

// ============================================================================
// CANDIDATE SEARCH SERVICES
// ============================================================================
// CANDIDATE SEARCH SERVICES
// ============================================================================

// Helper function to calculate match score between candidate and job
function calculateMatchScore(candidate, job) {
  let score = 0;
  
  // Match based on skills overlap
  if (job.skills && Array.isArray(job.skills) && candidate.skills && Array.isArray(candidate.skills)) {
    const matchingSkills = job.skills.filter(skill => 
      candidate.skills.some(candidateSkill => 
        candidateSkill.toLowerCase() === skill.toLowerCase()
      )
    );
    
    // Award points based on percentage of matching skills
    if (job.skills.length > 0) {
      score += (matchingSkills.length / job.skills.length) * 70; // Up to 70 points for skills match
    }
  }
  
  // Add points for experience (up to 30 points)
  if (candidate.experience_years) {
    // More experience = higher score, but with diminishing returns after 5 years
    score += Math.min(30, candidate.experience_years * 6);
  }
  
  // Normalize to 0-100 scale
  return Math.min(100, Math.round(score));
}

export const candidateService = {
  // Search candidates with comprehensive filtering
  searchCandidates: async (filters = {}) => {
    try {
      // Check if we should apply recently logged-in filter
      // Apply this when no specific search criteria are provided
      const hasSearchCriteria = !!(
        (filters.query && filters.query.trim()) || 
        (filters.location && filters.location.trim()) ||
        (filters.skills && Array.isArray(filters.skills) && filters.skills.length > 0) ||
        filters.experience_level ||
        filters.hourly_rate_min || 
        filters.hourly_rate_max ||
        filters.availability ||
        filters.rating_min
      );
      
      // Build the base query with proper column selection
      let query = supabase
        .from('user_profiles')
        .select(`
          id,
          full_name,
          email,
          title,
          bio,
          skills,
          experience_level,
          experience_years,
          hourly_rate,
          location,
          timezone,
          availability_status,
          languages,
          is_verified,
          is_active,
          identity_verification_status,
          profile_picture,
          public_username,
          preferred_project_types,
          rating,
          total_reviews,
          video_introduction_url,
          ai_interview_sessions!ai_interview_sessions_va_id_fkey(id, status, is_public),
          created_at,
          updated_at,
          last_login
        `, { count: 'exact' })
        .eq('role', 'va')
        .eq('is_active', true)
        .not('email', 'eq', 'test.admin@vasprouts.com') // Exclude the test admin user specifically
        .not('email', 'ilike', '%admin%') // Exclude any other admin emails
        .not('full_name', 'ilike', '%admin%'); // Exclude admin names
        
      // Apply text search across multiple fields
      if (filters.query) {
        // Security: Escape special characters to prevent ILIKE pattern injection
        const searchTerm = escapePostgresLike(filters.query.trim());
        query = query.or(`full_name.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%,bio.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`);
      }

      // Apply location filter (case insensitive)
      if (filters.location) {
        // Security: Escape special characters to prevent ILIKE pattern injection
        const escapedLocation = escapePostgresLike(filters.location);
        query = query.ilike('location', `%${escapedLocation}%`);
      }

      // Apply skills filter (array overlap)
      if (filters.skills && Array.isArray(filters.skills) && filters.skills.length > 0) {
        try {
          console.log('Applying skills filter with skills:', filters.skills);
          
          // Use the database function to find candidates with matching skills
          const { data: matchingUsers, error } = await supabase
            .rpc('search_by_skills', { skill_array: filters.skills });
          
          if (error) {
            console.error('Error using search_by_skills function:', error);
            
            // Fallback to our hardcoded map if the function fails
            console.log('Falling back to hardcoded skill map');
            
            // Hardcoded skill map as fallback
            const skillMap = {
              'Customer Service': ['f8b3f740-c1a7-45b9-bc52-bfd623f00880', '43f40c79-8d98-4fc6-94c6-aebf89b077d8'],
              'Project Management': ['bee265db-7cbc-4c70-bd95-d72d8a669ed6', 'fe47fb33-42bd-4aeb-90f2-eeced380b195'],
              'Email Management': ['f8b3f740-c1a7-45b9-bc52-bfd623f00880', '43f40c79-8d98-4fc6-94c6-aebf89b077d8'],
              'Web Development': ['fe47fb33-42bd-4aeb-90f2-eeced380b195', 'bee265db-7cbc-4c70-bd95-d72d8a669ed6', '68cd8e3e-3f9d-43b8-8e8e-a7f3b41dfb19'],
              'WordPress': ['68cd8e3e-3f9d-43b8-8e8e-a7f3b41dfb19'],
              'Admin Assistant': ['f8b3f740-c1a7-45b9-bc52-bfd623f00880'],
              'SEO': ['60ac4f0b-0b1e-4906-b69d-50d48cc54611'],
              'SAAS': ['68cd8e3e-3f9d-43b8-8e8e-a7f3b41dfb19'],
              'System Administration': ['43f40c79-8d98-4fc6-94c6-aebf89b077d8'],
              'User Management': ['43f40c79-8d98-4fc6-94c6-aebf89b077d8'],
              'Platform Security': ['43f40c79-8d98-4fc6-94c6-aebf89b077d8'],
              'Data Analytics': ['43f40c79-8d98-4fc6-94c6-aebf89b077d8']
            };
            
            // Get matching user IDs for all selected skills
            const matchingUserIds = new Set();
            
            // For each selected skill
            for (const skill of filters.skills) {
              if (skill && skill.trim()) {
                const trimmedSkill = skill.trim();
                console.log(`Looking for candidates with skill: ${trimmedSkill}`);
                
                // Check if we have hardcoded candidates for this skill
                const candidatesWithSkill = skillMap[trimmedSkill] || [];
                
                if (candidatesWithSkill.length > 0) {
                  console.log(`Found ${candidatesWithSkill.length} candidates with skill: ${trimmedSkill}`);
                  candidatesWithSkill.forEach(userId => matchingUserIds.add(userId));
                } else {
                  console.log(`No candidates found with skill: ${trimmedSkill}`);
                }
              }
            }
            
            // Apply the filter to our main query
            if (matchingUserIds.size > 0) {
              console.log(`Found ${matchingUserIds.size} total candidates matching skills criteria`);
              query = query.in('id', Array.from(matchingUserIds));
            } else {
              console.log('No candidates found matching skills criteria');
              // No matches, add an impossible condition to return empty set
              query = query.eq('id', '00000000-0000-0000-0000-000000000000');
            }
          } else {
            // Successfully got results from the database function
            const matchingUserIds = matchingUsers || [];
            
            if (matchingUserIds.length > 0) {
              console.log(`Found ${matchingUserIds.length} total candidates matching skills criteria`);
              query = query.in('id', matchingUserIds);
            } else {
              console.log('No candidates found matching skills criteria');
              // No matches, add an impossible condition to return empty set
              query = query.eq('id', '00000000-0000-0000-0000-000000000000');
            }
          }
        } catch (skillsError) {
          console.error('Skills filtering error:', skillsError);
          // Continue without skills filter, but note this in logs
          console.warn('Continuing search without skills filter due to error');
        }
      }

      // Apply experience level filter
      if (filters.experience_level) {
        query = query.eq('experience_level', filters.experience_level);
      }

      // Apply hourly rate filters
      if (filters.hourly_rate_min && !isNaN(parseFloat(filters.hourly_rate_min))) {
        query = query.gte('hourly_rate', parseFloat(filters.hourly_rate_min));
      }
      if (filters.hourly_rate_max && !isNaN(parseFloat(filters.hourly_rate_max))) {
        query = query.lte('hourly_rate', parseFloat(filters.hourly_rate_max));
      }

      // Apply availability filter
      if (filters.availability) {
        query = query.eq('availability_status', filters.availability);
      }

      // Apply minimum experience years (for rating_min, we'll use experience_years as proxy)
      if (filters.rating_min && !isNaN(parseFloat(filters.rating_min))) {
        const minExperience = Math.floor(parseFloat(filters.rating_min));
        query = query.gte('experience_years', minExperience);
      }

      // Apply verified filter
      if (filters.verified_only) {
        query = query.eq('is_verified', true);
      }

      // Apply active filter (already applied above but keeping for consistency)
      if (filters.active_only) {
        query = query.eq('is_active', true);
      }

      // Apply sorting
      const sortBy = filters.sort_by || 'updated_at';
      const sortOrder = filters.sort_order === 'asc' ? { ascending: true } : { ascending: false };
      
      // Map to valid database columns
      const validSortColumns = {
        'rating': 'experience_years', // Use experience as proxy for rating
        'created_at': 'created_at',
        'updated_at': 'updated_at',
        'hourly_rate': 'hourly_rate',
        'experience_years': 'experience_years'
      };
      
      const dbSortColumn = validSortColumns[sortBy] || 'updated_at';
      query = query.order(dbSortColumn, sortOrder);

      // Apply pagination
      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
      } else if (filters.limit) {
        query = query.limit(filters.limit);
      }
      
      try {
        const { data, error, count } = await query;
        
        if (error) {
          console.error('Database query error:', error);
          console.debug('Query details:', {
            filters: JSON.stringify(filters),
            query: query.toString() // For debugging only
          });
          throw error;
        }

        // If no data returned, handle gracefully
        if (!data || !Array.isArray(data)) {
          console.warn('Empty or invalid data returned from database');
          return { 
            candidates: [], 
            total: 0,
            hasMore: false,
            error: null 
          };
        }

        const candidateCount = data.length || 0;
        
        // If no candidates found, return empty result with proper structure
        if (candidateCount === 0) {
          return { 
            candidates: [], 
            total: 0,
            hasMore: false,
            error: null 
          };
        }

        // Filter out suspended/banned candidates from results
        const candidateIds = data.map(c => c.id);
        const { data: moderationStatuses } = await supabase
          .from('user_moderation_status')
          .select('user_id, status')
          .in('user_id', candidateIds);

        const bannedUserIds = new Set(
          (moderationStatuses || [])
            .filter(m => m.status === 'suspended' || m.status === 'banned')
            .map(m => m.user_id)
        );

        const filteredData = data.filter(candidate => !bannedUserIds.has(candidate.id));
        
        // Transform data to match expected format
        const transformedCandidates = (filteredData || []).map(candidate => ({
          ...candidate,
          // Ensure skills is always an array
          skills: Array.isArray(candidate.skills) ? candidate.skills : [],
          // Ensure languages is always an array  
          languages: Array.isArray(candidate.languages) ? candidate.languages : [],
          // Add computed fields for UI compatibility
          rating: Math.min(5, Math.max(1, candidate.experience_years * 0.8 + 3.5)), // Computed rating
          reviews_count: Math.floor(candidate.experience_years * 10), // Computed reviews
          response_rate: '95%', // Default response rate
          success_rate: '96%', // Default success rate
          completed_jobs: Math.floor(candidate.experience_years * 20), // Computed completed jobs
          availability: candidate.availability_status || 'available'
        }));

        // Calculate total count and pagination info
        const total = count || transformedCandidates.length;
        const hasMore = filters.offset ? 
          (filters.offset + transformedCandidates.length < total) : 
          (transformedCandidates.length >= (filters.limit || 10));

        return { 
          candidates: transformedCandidates, 
          total,
          hasMore,
          error: null 
        };
      } catch (queryError) {
        // If the complex query fails, fall back to a simpler query
        try {
          // Try a much simpler query without the complex filters
          const fallbackQuery = supabase
            .from('user_profiles')
            .select('*')
            .eq('role', 'va')
            .eq('is_active', true)
            .limit(filters.limit || 10);
            
          // Add name search if provided
          if (filters.query) {
            // Security: Escape special characters to prevent ILIKE pattern injection
            const escapedQuery = escapePostgresLike(filters.query);
            fallbackQuery.ilike('full_name', `%${escapedQuery}%`);
          }
          
          const { data: fallbackData, error: fallbackError } = await fallbackQuery;
          
          if (fallbackError) throw fallbackError;
          
          // Transform even the fallback data
          const transformedFallback = (fallbackData || []).map(candidate => ({
            ...candidate,
            skills: Array.isArray(candidate.skills) ? candidate.skills : [],
            languages: Array.isArray(candidate.languages) ? candidate.languages : [],
            rating: Math.min(5, Math.max(1, candidate.experience_years * 0.8 + 3.5)),
            reviews_count: Math.floor(candidate.experience_years * 10),
            response_rate: '95%',
            success_rate: '96%',
            completed_jobs: Math.floor(candidate.experience_years * 20),
            availability: candidate.availability_status || 'available'
          }));
          
          return {
            candidates: transformedFallback,
            total: transformedFallback.length,
            hasMore: false,
            error: null
          };
        } catch (fallbackError) {
          // If even the fallback fails, return the original error
          console.error('Search candidates error:', queryError);
          return { 
            candidates: [], 
            total: 0,
            hasMore: false,
            error: queryError.message 
          };
        }
      }
    } catch (error) {
      console.error('Search candidates error:', error);
      return { 
        candidates: [], 
        total: 0,
        hasMore: false,
        error: error.message 
      };
    }
  },

  // Get candidate profile by ID
  getCandidateById: async (candidateId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', candidateId)
        .eq('role', 'va')
        .single();
      
      if (error) throw error;
      return { candidate: data, error: null };
    } catch (error) {
      console.error('Get candidate by ID error:', error);
      return { candidate: null, error: error.message };
    }
  },

  // Get AI recommendations for a job
  getAIRecommendations: async (jobId) => {
    try {
      // First fetch the job details to get skills
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('skills, title, description')
        .eq('id', jobId)
        .single();
        
      if (jobError) throw jobError;
      
      // If no job found or no skills defined, return empty result
      if (!job || !job.skills || !Array.isArray(job.skills) || job.skills.length === 0) {
        return { candidates: [], total: 0, error: 'No skills defined for this job' };
      }
      
      // Create a query to find candidates with matching skills
      const query = supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'va')
        .eq('is_active', true);
        
      // Use the skills contains operation for each skill
      // Temporary workaround for array operations
      let matchingUserIds = new Set();
      let isFirstSkill = true;
      
      for (const skill of job.skills) {
        if (skill && skill.trim()) {
          try {
            const { data: skillMatches, error: skillError } = await supabase
              .from('user_profiles')
              .select('id')
              .eq('role', 'va')
              .eq('is_active', true)
              .filter('skills', 'cs', `["${skill.trim()}"]`);
              
            if (skillError) throw skillError;
            
            // For the first skill, initialize the set. For subsequent skills, find common IDs
            if (isFirstSkill) {
              skillMatches.forEach(user => matchingUserIds.add(user.id));
              isFirstSkill = false;
            } else {
              // For an "OR" match logic, just add all IDs
              skillMatches.forEach(user => matchingUserIds.add(user.id));
            }
          } catch (error) {
            console.error(`Error matching skill ${skill}:`, error);
          }
        }
      }
      
      // If we have matching users, fetch their full profiles
      if (matchingUserIds.size > 0) {
        const { data: candidates, error: candidateError } = await supabase
          .from('user_profiles')
          .select('*')
          .in('id', Array.from(matchingUserIds))
          .order('experience_years', { ascending: false })
          .limit(10);
          
        if (candidateError) throw candidateError;
        
        // Transform the candidates similar to the search function
        const transformedCandidates = candidates.map(candidate => ({
          ...candidate,
          skills: Array.isArray(candidate.skills) ? candidate.skills : [],
          languages: Array.isArray(candidate.languages) ? candidate.languages : [],
          rating: Math.min(5, Math.max(1, candidate.experience_years * 0.8 + 3.5)),
          match_score: calculateMatchScore(candidate, job)
        }));
        
        // Sort by match score
        transformedCandidates.sort((a, b) => b.match_score - a.match_score);
        
        return { candidates: transformedCandidates, total: transformedCandidates.length, error: null };
      } else {
        return { candidates: [], total: 0, error: null };
      }
    } catch (error) {
      console.error('Get AI recommendations error:', error);
      return { candidates: [], total: 0, error: error.message };
    }
  }
};
// ============================================================================

export const applicationService = {
  // Get applications for a job
  getJobApplications: async (jobId, filters = {}) => {
    try {
      let query = supabase
        .from('job_applications')
        .select(`
          *,
          user_profiles!job_applications_applicant_id_fkey(
            full_name, email, profile_picture, skills, hourly_rate, location, bio
          )
        `)
        .eq('job_id', jobId)
        .order('applied_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return { applications: data, error: null };
    } catch (error) {
      console.error('Get job applications error:', error);
      return { applications: [], error: error.message };
    }
  },

  // Update application status
  updateApplicationStatus: async (applicationId, status, notes = null) => {
    try {
      const updates = { 
        status, 
        updated_at: new Date().toISOString() 
      };
      
      if (notes) {
        updates.notes = notes;
      }

      const { data, error } = await supabase
        .from('job_applications')
        .update(updates)
        .eq('id', applicationId)
        .select()
        .single();
      
      if (error) throw error;
      return { application: data, error: null };
    } catch (error) {
      console.error('Update application status error:', error);
      return { application: null, error: error.message };
    }
  },

  // Schedule interview
  scheduleInterview: async (applicationId, interviewData) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .update({
          interview_scheduled_at: interviewData.scheduled_at,
          status: 'interviewed',
          employer_notes: interviewData.notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId)
        .select()
        .single();
      
      if (error) throw error;
      return { application: data, error: null };
    } catch (error) {
      console.error('Schedule interview error:', error);
      return { application: null, error: error.message };
    }
  },

  // Get employer's application analytics
  getApplicationAnalytics: async (employerId) => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          status,
          applied_at,
          jobs!inner(employer_id)
        `)
        .eq('jobs.employer_id', employerId);
      
      if (error) throw error;

      // Process analytics data
      const analytics = {
        total_applications: data.length,
        pending: data.filter(app => app.status === 'pending').length,
        shortlisted: data.filter(app => app.status === 'shortlisted').length,
        interviewed: data.filter(app => app.status === 'interviewed').length,
        accepted: data.filter(app => app.status === 'accepted').length,
        rejected: data.filter(app => app.status === 'rejected').length,
        recent_applications: data.filter(app => {
          const appDate = new Date(app.applied_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return appDate >= weekAgo;
        }).length
      };
      
      return { analytics, error: null };
    } catch (error) {
      console.error('Get application analytics error:', error);
      return { analytics: null, error: error.message };
    }
  }
};

// ============================================================================
// MESSAGING SERVICES
// ============================================================================

export const messagingService = {
  // Get conversations for user
  getConversations: async (userId) => {
    try {
      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!userId || !uuidRegex.test(userId)) {
        console.error('Invalid UUID format for user ID:', userId);
        return { conversations: [], error: 'Invalid user ID format' };
      }

      const { data, error } = await supabase
        .rpc('get_user_conversations', { user_id_param: userId });
      
      if (error) throw error;
      return { conversations: data || [], error: null };
    } catch (error) {
      console.error('Get conversations error:', error);
      return { conversations: [], error: error.message };
    }
  },

  // Get messages for a conversation
  getMessages: async (conversationId) => {
    try {
      // Ensure conversationId is a string
      const stringConversationId = String(conversationId);
      
      const { data, error } = await supabase
        .from('messages')
        .select('id, sender_id, content, sent_at, message_type, is_read, attachments, sender:user_profiles!messages_sender_id_fkey(full_name, profile_picture)')
        .eq('conversation_id', stringConversationId)
        .order('sent_at', { ascending: true });
      
      if (error) throw error;
      
      // Format messages for display
      const formattedMessages = data.map(msg => ({
        id: msg.id,
        senderId: msg.sender_id,
        senderName: msg.sender?.full_name || 'Unknown User',
        senderAvatar: msg.sender?.profile_picture || null,
        content: msg.content,
        timestamp: msg.sent_at,
        type: msg.message_type || 'text',
        isRead: msg.is_read,
        attachments: msg.attachments
      }));
      
      return { messages: formattedMessages, error: null };
    } catch (error) {
      console.error('Get messages error:', error);
      return { messages: [], error: error.message };
    }
  },

  // Mark messages as read
  markMessagesAsRead: async (conversationId, userId) => {
    try {
      // Get the conversation to check last message
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('id, last_message_sender_id')
        .eq('id', conversationId)
        .single();
      
      if (convError) throw convError;
      
      const readAt = new Date().toISOString();
      
      // Ensure conversationId is a string
      const stringConversationId = String(conversationId);
      
      // Update all unread messages in this conversation sent to this user
      const { error } = await supabase
        .from('messages')
        .update({ 
          is_read: true
        })
        .eq('conversation_id', stringConversationId)
        .eq('recipient_id', userId)
        .eq('is_read', false);
      
      if (error) throw error;
      
      // Note: Conversation status update removed since columns don't exist
      // The is_read status on individual messages is sufficient
      
      return { success: true, error: null };
    } catch (error) {
      console.error('Mark messages as read error:', error);
      return { success: false, error: error.message };
    }
  },

  // Subscribe to new messages and status updates in a conversation
  subscribeToMessages: (conversationId, callback) => {
    try {
      // Ensure conversationId is a string and validate format
      const stringConversationId = String(conversationId);
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(stringConversationId)) {
        console.error('Invalid UUID format for conversation subscription:', stringConversationId);
        return null;
      }
      
      const channel = supabase
        .channel(`messages:${stringConversationId}`)
        // Listen for new messages
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${stringConversationId}`
        }, async (payload) => {
          try {
            // Fetch the new message with user data
            const { data: newMessageWithUser } = await supabase
              .from('messages')
              .select('*, sender:user_profiles!messages_sender_id_fkey(full_name, profile_picture)')
              .eq('id', payload.new.id)
              .single();
              
            if (newMessageWithUser) {
              const formattedMessage = {
                id: newMessageWithUser.id,
                senderId: newMessageWithUser.sender_id,
                senderName: newMessageWithUser.sender?.full_name || 'Unknown User',
                senderAvatar: newMessageWithUser.sender?.profile_picture || null,
                content: newMessageWithUser.content,
                timestamp: newMessageWithUser.sent_at,
                type: newMessageWithUser.message_type || 'text',
                isRead: newMessageWithUser.is_read,
                attachments: newMessageWithUser.attachments
              };
              
              callback(formattedMessage);
            }
          } catch (error) {
            console.error('Error processing new message:', error);
          }
        })
        // Listen for message status updates (read, delivered)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${stringConversationId}`
        }, async (payload) => {
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('Message status updated:', payload.new.id);
            }
            // Check if read status changed
            if (payload.new.is_read !== payload.old.is_read) {
              
              // Fetch the updated message with user data
              const { data: updatedMessageWithUser } = await supabase
                .from('messages')
                .select('*, sender:user_profiles!messages_sender_id_fkey(full_name, profile_picture)')
                .eq('id', payload.new.id)
                .single();
                
              if (updatedMessageWithUser) {
                const formattedMessage = {
                  id: updatedMessageWithUser.id,
                  senderId: updatedMessageWithUser.sender_id,
                  senderName: updatedMessageWithUser.sender?.full_name || 'Unknown User',
                  senderAvatar: updatedMessageWithUser.sender?.profile_picture || null,
                  content: updatedMessageWithUser.content,
                  timestamp: updatedMessageWithUser.sent_at,
                  type: updatedMessageWithUser.message_type || 'text',
                  isRead: updatedMessageWithUser.is_read,
                  attachments: updatedMessageWithUser.attachments
                };
                
                // Notify with status update
                callback(formattedMessage, 'status_update');
              }
            }
          } catch (error) {
            console.error('Error processing message status update:', error);
          }
        })
        .subscribe({
          status: (status) => {
            if (process.env.NODE_ENV === 'development') {
              console.log(`Subscription status for messages:${conversationId}:`, status);
            }
          },
          error: (error) => {
            console.error(`Error in messages subscription for ${conversationId}:`, error);
          }
        });
      
      return channel;
    } catch (error) {
      console.error('Failed to set up message subscription:', error);
      return null;
    }
  },

  // Subscribe to conversation updates (new messages, read status changes)
  subscribeToConversations: (userId, callback) => {
    try {
      console.log(`Setting up subscription for conversations of user: ${userId}`);
      const channel = supabase
        .channel(`user-conversations:${userId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`
        }, async (payload) => {
          try {
            console.log('New message notification for conversation update:', payload.new.id);
            // When a new message is received, fetch updated conversations
            const { conversations } = await messagingService.getConversations(userId);
            callback(conversations);
          } catch (error) {
            console.error('Error processing conversation update from new message:', error);
          }
        })
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: 'participant_1.eq.' + userId + ',participant_2.eq.' + userId
        }, async (payload) => {
          try {
            console.log('Conversation updated:', payload.new.id);
            // When a conversation is updated, fetch updated conversations
            const { conversations } = await messagingService.getConversations(userId);
            callback(conversations);
          } catch (error) {
            console.error('Error processing conversation update:', error);
          }
        })
        .subscribe({
          status: (status) => {
            console.log(`Subscription status for user-conversations:${userId}:`, status);
          },
          error: (error) => {
            console.error(`Error in conversations subscription for ${userId}:`, error);
          }
        });
      
      return channel;
    } catch (error) {
      console.error('Failed to set up conversation subscription:', error);
      return null;
    }
  },

  // Unsubscribe from a channel
  unsubscribe: (subscription) => {
    try {
      if (subscription) {
        console.log('Unsubscribing from channel:', subscription.topic);
        supabase.removeChannel(subscription);
      }
    } catch (error) {
      console.error('Error unsubscribing from channel:', error);
    }
  },
  
  // Send typing status (typing started or stopped)
  sendTypingStatus: async (conversationId, userId, recipientId, isTyping) => {
    try {
      const channel = supabase.channel(`typing:${conversationId}`);
      
      const status = await channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: {
          conversation_id: conversationId,
          user_id: userId,
          recipient_id: recipientId,
          is_typing: isTyping,
          timestamp: new Date().toISOString()
        }
      });
      
      // Remove the channel after sending the message (to avoid channel accumulation)
      setTimeout(() => {
        supabase.removeChannel(channel);
      }, 1000);
      
      return { success: true, status };
    } catch (error) {
      console.error('Error sending typing status:', error);
      return { success: false, error: error.message };
    }
  },
  
  // Subscribe to typing status changes
  subscribeToTypingStatus: (conversationId, callback) => {
    try {
      console.log(`Setting up subscription for typing status in conversation: ${conversationId}`);
      
      const channel = supabase
        .channel(`typing:${conversationId}`)
        .on('broadcast', { event: 'typing' }, (payload) => {
          console.log('Typing status update received:', payload);
          
          // Process the typing status data
          const typingData = {
            conversationId: payload.payload.conversation_id,
            userId: payload.payload.user_id,
            recipientId: payload.payload.recipient_id,
            isTyping: payload.payload.is_typing,
            timestamp: payload.payload.timestamp
          };
          
          // Notify the component with the typing status data
          callback(typingData);
        })
        .subscribe({
          status: (status) => {
            console.log(`Subscription status for typing:${conversationId}:`, status);
          },
          error: (error) => {
            console.error(`Error in typing subscription for ${conversationId}:`, error);
          }
        });
      
      return channel;
    } catch (error) {
      console.error('Failed to set up typing subscription:', error);
      return null;
    }
  },

  // Send message
  sendMessage: async (conversationId, content, senderId, messageType = 'text', attachmentData = null) => {
    try {
      // First get the conversation to determine recipient
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('participant_1, participant_2')
        .eq('id', conversationId)
        .single();
      
      if (convError) throw convError;
      
      // Determine recipient ID
      const recipientId = conversation.participant_1 === senderId 
        ? conversation.participant_2 
        : conversation.participant_1;
      
      // Create message object
      const messageData = {
        conversation_id: conversationId,
        sender_id: senderId,
        recipient_id: recipientId,
        content,
        message_type: messageType,
        is_read: false,
        sent_at: new Date().toISOString(),
        attachments: attachmentData
      };
      
      // Insert message into database
      const { data: message, error: messageError } = await supabase
        .from('messages')
        .insert(messageData)
        .select()
        .single();
      
      if (messageError) throw messageError;
      
      // Update the conversation's last_message_at timestamp and last message info
      const { error: updateError } = await supabase
        .from('conversations')
        .update({
          last_message_at: messageData.sent_at
        })
        .eq('id', conversationId);
      
      if (updateError) throw updateError;
      
      // Note: Delivery status simulation removed since columns don't exist
      
      return { message, error: null };
    } catch (error) {
      console.error('Send message error:', error);
      return { message: null, error: error.message };
    }
  },

  // Create or get existing conversation between two users
  createOrGetConversation: async (userId1, userId2) => {
    try {
      // First, try to find existing conversation
      // Conversations are unified by participant pair only (no job_id filtering)
      // Single .or() call with proper Supabase PostgREST syntax
      const { data: existingConversation, error: searchError } = await supabase
        .from('conversations')
        .select('*')
        .or(`and(participant_1.eq.${userId1},participant_2.eq.${userId2}),and(participant_1.eq.${userId2},participant_2.eq.${userId1})`)
        .single();
      
      if (existingConversation && !searchError) {
        return { conversation: existingConversation, error: null };
      }

      // PGRST116 is "no rows found" - this is expected when creating a new conversation
      if (searchError && searchError.code !== 'PGRST116') {
        console.warn('Error searching for existing conversation:', searchError);
      }

      // If no existing conversation, create new one
      const { data: newConversation, error: createError } = await supabase
        .from('conversations')
        .insert({
          participant_1: userId1,
          participant_2: userId2,
          last_message_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (createError) throw createError;
      return { conversation: newConversation, error: null };
    } catch (error) {
      console.error('Create or get conversation error:', error);
      return { conversation: null, error: error.message };
    }
  }
};

// ============================================================================
// ANALYTICS SERVICES
// ============================================================================

export const analyticsService = {
  // Get employer dashboard analytics
  getEmployerAnalytics: async (employerId) => {
    try {
      // Get jobs analytics
      const { data: jobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id, status, created_at, views_count, applications_count')
        .eq('employer_id', employerId);
      
      if (jobsError) throw jobsError;

      // Get applications analytics
      const { data: applications, error: appsError } = await supabase
        .from('job_applications')
        .select(`
          status, applied_at, match_percentage,
          jobs!inner(employer_id)
        `)
        .eq('jobs.employer_id', employerId);
      
      if (appsError) throw appsError;

      // Calculate metrics
      const analytics = {
        jobs: {
          total: jobs.length,
          active: jobs.filter(job => job.status === 'published').length,
          draft: jobs.filter(job => job.status === 'draft').length,
          filled: jobs.filter(job => job.status === 'filled').length,
          total_views: jobs.reduce((sum, job) => sum + (job.views_count || 0), 0),
          total_applications: jobs.reduce((sum, job) => sum + (job.applications_count || 0), 0)
        },
        applications: {
          total: applications.length,
          pending: applications.filter(app => app.status === 'pending').length,
          shortlisted: applications.filter(app => app.status === 'shortlisted').length,
          interviewed: applications.filter(app => app.status === 'interviewed').length,
          accepted: applications.filter(app => app.status === 'accepted').length,
          rejected: applications.filter(app => app.status === 'rejected').length,
          avg_match_score: applications.length > 0 
            ? applications.reduce((sum, app) => sum + (app.match_percentage || 0), 0) / applications.length 
            : 0
        },
        trends: {
          applications_this_week: applications.filter(app => {
            const appDate = new Date(app.applied_at);
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return appDate >= weekAgo;
          }).length,
          jobs_this_month: jobs.filter(job => {
            const jobDate = new Date(job.created_at);
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return jobDate >= monthAgo;
          }).length
        }
      };
      
      return { analytics, error: null };
    } catch (error) {
      console.error('Get employer analytics error:', error);
      return { analytics: null, error: error.message };
    }
  },

  // Get hiring funnel analytics
  getHiringFunnelAnalytics: async (employerId, timeRange = '30d') => {
    try {
      const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const { data: applications, error } = await supabase
        .from('job_applications')
        .select(`
          status, applied_at,
          jobs!inner(employer_id, title)
        `)
        .eq('jobs.employer_id', employerId)
        .gte('applied_at', startDate.toISOString());
      
      if (error) throw error;

      const funnel = {
        applied: applications.length,
        shortlisted: applications.filter(app => ['shortlisted', 'interviewed', 'accepted'].includes(app.status)).length,
        interviewed: applications.filter(app => ['interviewed', 'accepted'].includes(app.status)).length,
        accepted: applications.filter(app => app.status === 'accepted').length
      };

      // Calculate conversion rates
      funnel.shortlist_rate = funnel.applied > 0 ? (funnel.shortlisted / funnel.applied * 100) : 0;
      funnel.interview_rate = funnel.shortlisted > 0 ? (funnel.interviewed / funnel.shortlisted * 100) : 0;
      funnel.acceptance_rate = funnel.interviewed > 0 ? (funnel.accepted / funnel.interviewed * 100) : 0;
      
      return { funnel, error: null };
    } catch (error) {
      console.error('Get hiring funnel analytics error:', error);
      return { funnel: null, error: error.message };
    }
  },

  // Track analytics events - LOCAL ONLY VERSION (no database interactions)
  trackEvent: async (eventName, eventData = {}, userId = null) => {
    try {
      // Get current user if not provided
      if (!userId) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          userId = user?.id;
        } catch (userError) {
          userId = 'anonymous';
        }
      }

      // Create event record - for local tracking only
      const eventRecord = {
        event_type: eventName,
        user_id: userId || 'anonymous',
        created_at: new Date().toISOString(),
      };

      // Safely handle the event data
      try {
        eventRecord.event_data = JSON.parse(JSON.stringify(eventData));
      } catch (e) {
        eventRecord.event_data = { 
          note: 'Original data could not be stringified',
          timestamp: new Date().toISOString()
        };
      }

      // Only log to console in development - no database interactions
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics]', eventName, eventRecord);
      }
      
      // Return success
      return { event: eventRecord, error: null };
    } catch (error) {
      // Even if tracking fails, we don't want to impact the application
      console.warn('Non-critical analytics error:', error.message);
      return { 
        event: { event_type: eventName, timestamp: new Date().toISOString() }, 
        error: null 
      };
    }
  }
};

// ============================================================================
// TEAM MANAGEMENT SERVICES
// ============================================================================

export const teamService = {
  // Get team members for employer
  getTeamMembers: async (employerId) => {
    try {
      // This would require a team_members table - implementing basic structure
      return { 
        members: [
          {
            id: '1',
            name: 'John Smith',
            email: 'john@company.com',
            role: 'Manager',
            status: 'active',
            joined_at: new Date().toISOString()
          }
        ], 
        error: null 
      };
    } catch (error) {
      console.error('Get team members error:', error);
      return { members: [], error: error.message };
    }
  },

  // Invite team member
  inviteTeamMember: async (employerId, inviteData) => {
    try {
      // This would require team invitation logic
      const invitation = {
        id: Date.now().toString(),
        email: inviteData.email,
        role: inviteData.role,
        invited_by: employerId,
        invited_at: new Date().toISOString(),
        status: 'pending'
      };
      
      return { invitation, error: null };
    } catch (error) {
      console.error('Invite team member error:', error);
      return { invitation: null, error: error.message };
    }
  }
};

// ============================================================================
// INTERVIEW MANAGEMENT SERVICES
// ============================================================================

export const interviewService = {
  // Get employer's interviews from interview_schedules table
  getEmployerInterviews: async (employerId) => {
    try {
      // Get interviews directly by interviewer_id (employer) with all related data
      const { data, error } = await supabase
        .from('interview_schedules')
        .select(`
          id,
          application_id,
          candidate_id,
          interviewer_id,
          scheduled_at,
          duration_minutes,
          interview_type,
          meeting_link,
          notes,
          status,
          feedback,
          hiring_status,
          hiring_notes,
          considered_for_hire,
          job_title,
          candidate_name,
          candidate_email,
          created_at,
          updated_at,
          candidate:user_profiles!interview_schedules_candidate_id_fkey (
            full_name,
            email,
            profile_picture
          ),
          job_applications (
            job_id,
            job:jobs (
              id,
              title,
              employer_id
            )
          )
        `)
        .eq('interviewer_id', employerId)
        .order('scheduled_at', { ascending: true });

      if (error) throw error;

      // Transform data to match expected interview format
      const interviews = data?.map(interview => {
        const scheduledAt = new Date(interview.scheduled_at);
        
        // Parse feedback JSON if exists
        let feedbackData = {};
        let rating = null;
        if (interview.feedback) {
          try {
            // If feedback is already an object, use it directly
            if (typeof interview.feedback === 'object' && interview.feedback !== null) {
              feedbackData = interview.feedback;
            } else if (typeof interview.feedback === 'string') {
              feedbackData = JSON.parse(interview.feedback);
            } else {
              feedbackData = { feedback: String(interview.feedback) };
            }
            rating = feedbackData.rating || null;
          } catch (e) {
            // If JSON parsing fails, treat it as plain text feedback
            console.debug('Interview feedback is not valid JSON, treating as plain text:', {
              interviewId: interview.id,
              feedbackType: typeof interview.feedback
            });
            feedbackData = { 
              feedback: String(interview.feedback),
              rating: null 
            };
          }
        }
        
        // Get job title and job_id from multiple sources
        let jobTitle = 'Unknown Position';
        let jobId = null;
        if (interview.job_title) {
          jobTitle = interview.job_title;
        } else if (interview.job_applications?.job?.title) {
          jobTitle = interview.job_applications.job.title;
        }
        
        // Extract job_id from job_applications
        if (interview.job_applications?.job_id) {
          jobId = interview.job_applications.job_id;
        } else if (interview.job_applications?.job?.id) {
          jobId = interview.job_applications.job.id;
        }
        
        // Get candidate info from multiple sources (prioritize fresh data from user_profiles)
        let candidateName = 'Unknown';
        let candidateEmail = '';
        let candidateAvatar = null;
        
        if (interview.candidate?.full_name) {
          candidateName = interview.candidate.full_name;
        } else if (interview.candidate_name) {
          candidateName = interview.candidate_name;
        }
        
        if (interview.candidate?.email) {
          candidateEmail = interview.candidate.email;
        } else if (interview.candidate_email) {
          candidateEmail = interview.candidate_email;
        }
        
        if (interview.candidate?.profile_picture) {
          candidateAvatar = interview.candidate.profile_picture;
        }
        
        return {
          id: interview.id,
          candidate_name: candidateName,
          candidate_email: candidateEmail,
          candidate_avatar: candidateAvatar,
          job_title: jobTitle,
          scheduled_at: interview.scheduled_at, // Add the original timestamp
          scheduled_date: scheduledAt.toISOString().split('T')[0],
          scheduled_time: scheduledAt.toTimeString().slice(0, 5),
          duration: interview.duration_minutes || 60,
          type: interview.interview_type || 'video',
          status: interview.status || 'scheduled',
          meeting_link: interview.meeting_link || '',
          notes: interview.notes || '',
          // Preserve the entire feedback object
          feedback: feedbackData,
          rating: rating,
          // Include additional feedback fields for compatibility
          technical_skills: feedbackData.technical_skills || null,
          communication_skills: feedbackData.communication_skills || null,
          cultural_fit: feedbackData.cultural_fit || null,
          strengths: feedbackData.strengths || null,
          areas_for_improvement: feedbackData.areas_for_improvement || null,
          skills_assessment: feedbackData.skills_assessment || null,
          recommendation: feedbackData.recommendation || null,
          completed_at: feedbackData.completed_at || null,
          hiring_status: interview.hiring_status || 'interviewed',
          hiring_notes: interview.hiring_notes || '',
          considered_for_hire: interview.considered_for_hire || false,
          phone_number: null, // Will need separate query if needed
          application_id: interview.application_id,
          job_id: jobId,
          interviewer_id: interview.interviewer_id,
          candidate_id: interview.candidate_id,
          created_at: interview.created_at,
          updated_at: interview.updated_at
        };
      }) || [];

      return { interviews, error: null };
    } catch (error) {
      console.error('Get employer interviews error:', error);
      return { interviews: [], error: error.message };
    }
  },

  // Get all interviews for a specific candidate
  getCandidateInterviews: async (candidateId) => {
    try {
      const { data, error } = await supabase
        .from('interview_schedules')
        .select(`
          *,
          application:job_applications(
            id,
            status,
            job:jobs(
              id,
              title,
              company_name
            )
          ),
          interviewer:user_profiles!interview_schedules_interviewer_id_fkey(
            id,
            full_name,
            email,
            company_name
          )
        `)
        .eq('candidate_id', candidateId)
        .order('scheduled_at', { ascending: false });

      if (error) throw error;

      // Transform the data to parse feedback JSON
      const interviews = data?.map(interview => {
        // Parse feedback JSON if exists
        let feedbackData = {};
        if (interview.feedback) {
          try {
            // If feedback is already an object, use it directly
            if (typeof interview.feedback === 'object' && interview.feedback !== null) {
              feedbackData = interview.feedback;
            } else if (typeof interview.feedback === 'string') {
              feedbackData = JSON.parse(interview.feedback);
            } else {
              feedbackData = { feedback: String(interview.feedback) };
            }
          } catch (e) {
            // If JSON parsing fails, treat it as plain text feedback
            console.debug('Interview feedback is not valid JSON, treating as plain text:', {
              interviewId: interview.id,
              feedbackType: typeof interview.feedback
            });
            feedbackData = { 
              feedback: String(interview.feedback)
            };
          }
        }

        return {
          ...interview,
          // Preserve the entire feedback object
          feedback: feedbackData,
          // Include additional feedback fields for direct access
          rating: feedbackData.rating || null,
          technical_skills: feedbackData.technical_skills || null,
          communication_skills: feedbackData.communication_skills || null,
          cultural_fit: feedbackData.cultural_fit || null,
          strengths: feedbackData.strengths || null,
          areas_for_improvement: feedbackData.areas_for_improvement || null,
          skills_assessment: feedbackData.skills_assessment || null,
          recommendation: feedbackData.recommendation || null,
          completed_at: feedbackData.completed_at || null
        };
      }) || [];

      return { interviews, error: null };
    } catch (error) {
      console.error('Get candidate interviews error:', error);
      return { interviews: [], error: error.message };
    }
  },

  // Create a new interview schedule
  createInterview: async (interviewData) => {
    try {
      // Validate required fields
      if (!interviewData.candidate_id) {
        throw new Error('Candidate ID is required');
      }
      if (!interviewData.interviewer_id) {
        throw new Error('Interviewer ID is required');
      }
      // Support both separate date/time fields and a pre-built scheduled_at ISO string
      let scheduledAt;
      if (interviewData.scheduled_at) {
        scheduledAt = new Date(interviewData.scheduled_at);
      } else if (interviewData.date && interviewData.time) {
        scheduledAt = new Date(`${interviewData.date}T${interviewData.time}`);
      } else {
        throw new Error('Date and time are required');
      }
      
      // Validate the date
      if (isNaN(scheduledAt.getTime())) {
        throw new Error('Invalid date or time format');
      }

      // Get candidate and job information for denormalized fields
      let candidateInfo = null;
      let jobInfo = null;

      // Fetch candidate information
      const { data: candidateData, error: candidateError } = await supabase
        .from('user_profiles')
        .select('full_name, email')
        .eq('id', interviewData.candidate_id)
        .single();

      if (candidateError) {
        console.warn('Could not fetch candidate info:', candidateError);
      } else {
        candidateInfo = candidateData;
      }

      // Fetch job information if application_id is provided
      if (interviewData.application_id) {
        const { data: jobData, error: jobError } = await supabase
          .from('job_applications')
          .select(`
            job:jobs(title)
          `)
          .eq('id', interviewData.application_id)
          .single();

        if (jobError) {
          console.warn('Could not fetch job info:', jobError);
        } else {
          jobInfo = jobData?.job;
        }
      }

      const insertData = {
        application_id: interviewData.application_id,
        interviewer_id: interviewData.interviewer_id,
        candidate_id: interviewData.candidate_id,
        scheduled_at: scheduledAt.toISOString(),
        duration_minutes: interviewData.duration || 60,
        interview_type: interviewData.type || 'video',
        meeting_link: interviewData.meeting_link || null,
        notes: interviewData.notes || null,
        status: 'scheduled',
        // Add denormalized fields for better performance
        candidate_name: candidateInfo?.full_name || null,
        candidate_email: candidateInfo?.email || null,
        job_title: jobInfo?.title || interviewData.jobTitle || null
      };

      console.log('Creating interview with data:', insertData);

      const { data, error } = await supabase
        .from('interview_schedules')
        .insert(insertData)
        .select(`
          id,
          scheduled_at,
          duration_minutes,
          interview_type,
          meeting_link,
          notes,
          status,
          job_title,
          application:job_applications(
            id,
            job:jobs(
              id,
              title
            ),
            applicant:user_profiles!job_applications_applicant_id_fkey(
              id,
              full_name,
              email
            )
          )
        `)
        .single();

      if (error) throw error;

      // Update application status to interviewed if not already interviewed
      if (interviewData.application_id) {
        try {
          // First check current status
          const { data: currentApp, error: fetchError } = await supabase
            .from('job_applications')
            .select('status')
            .eq('id', interviewData.application_id)
            .single();

          if (fetchError) {
            console.warn('Could not fetch application for status update:', fetchError);
          } else if (currentApp && currentApp.status !== 'interviewed') {
            // Only update if status is not already 'interviewed'
            const { error: updateError } = await supabase
              .from('job_applications')
              .update({ 
                status: 'interviewed',
                interview_scheduled_at: scheduledAt.toISOString(),
                updated_at: new Date().toISOString()
              })
              .eq('id', interviewData.application_id);
            
            if (updateError) {
              console.warn('Could not update application status:', updateError);
            }
          }
        } catch (appUpdateError) {
          console.warn('Application status update failed (non-critical):', appUpdateError);
          // Don't fail the interview creation if application update fails
        }
      }

      // Send interview-scheduled email notification to the candidate
      try {
        if (candidateInfo?.email) {
          // Fetch employer name for the email
          let employerName = 'Employer';
          const { data: employerData } = await supabase
            .from('user_profiles')
            .select('full_name')
            .eq('id', interviewData.interviewer_id)
            .single();
          if (employerData?.full_name) {
            employerName = employerData.full_name;
          }

          const interviewDate = scheduledAt.toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          });
          const interviewTime = scheduledAt.toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit'
          });

          await emailService.sendInterviewScheduledNotification(
            candidateInfo.email,
            candidateInfo.full_name,
            {
              employerName,
              jobTitle: jobInfo?.title || interviewData.jobTitle || 'Virtual Assistant Position',
              interviewDate,
              interviewTime,
              interviewType: interviewData.type || 'video',
              interviewDuration: interviewData.duration || 60,
              interviewTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
              interviewNotes: interviewData.notes || ''
            }
          );
        }
      } catch (emailError) {
        console.warn('Failed to send interview scheduled email (non-critical):', emailError);
        // Don't fail the interview creation if email fails
      }

      return { interview: data, error: null };
    } catch (error) {
      console.error('Create interview error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Update interview status
  updateInterviewStatus: async (interviewId, status) => {
    try {
      const { data, error } = await supabase
        .from('interview_schedules')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', interviewId)
        .select()
        .single();

      if (error) throw error;
      return { interview: data, error: null };
    } catch (error) {
      console.error('Update interview status error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Update hiring status (separate from interview status)
  updateHiringStatus: async (interviewId, hiringStatus, additionalData = {}) => {
    try {
      const updates = {
        hiring_status: hiringStatus,
        decision_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Add optional fields based on hiring decision
      if (additionalData.notes) {
        updates.hiring_notes = additionalData.notes;
      }
      
      if (additionalData.offer_amount && hiringStatus === 'hired') {
        updates.offer_amount = additionalData.offer_amount;
      }
      
      if (additionalData.rejection_reason && hiringStatus === 'not_moving_forward') {
        updates.rejection_reason = additionalData.rejection_reason;
      }

      const { data, error } = await supabase
        .from('interview_schedules')
        .update(updates)
        .eq('id', interviewId)
        .select()
        .single();

      if (error) throw error;
      return { interview: data, error: null };
    } catch (error) {
      console.error('Update hiring status error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Complete interview with feedback
  completeInterview: async (interviewId, feedbackData = {}) => {
    try {
      const feedback = {
        feedback: feedbackData.feedback || '',
        rating: feedbackData.rating || null,
        technical_skills: feedbackData.technical_skills || null,
        communication_skills: feedbackData.communication_skills || null,
        cultural_fit: feedbackData.cultural_fit || null,
        strengths: feedbackData.strengths || null,
        areas_for_improvement: feedbackData.areas_for_improvement || null,
        recommendation: feedbackData.recommendation || null,
        completed_at: new Date().toISOString()
      };

      // Determine hiring status based on recommendation
      let hiringStatus = 'interviewed'; // default
      if (feedbackData.recommendation === 'hire') {
        hiringStatus = 'hired';
      } else if (feedbackData.recommendation === 'consider') {
        hiringStatus = 'under_consideration';
      } else if (feedbackData.recommendation === 'reject') {
        hiringStatus = 'not_moving_forward';
      }

      const updates = {
        status: 'completed',
        feedback: feedback,
        hiring_status: hiringStatus,
        updated_at: new Date().toISOString()
      };

      // Add decision_date if a hiring decision was made
      if (feedbackData.recommendation && feedbackData.recommendation !== 'consider') {
        updates.decision_date = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('interview_schedules')
        .update(updates)
        .eq('id', interviewId)
        .select(`
          id,
          application_id,
          feedback,
          status,
          hiring_status
        `)
        .single();

      if (error) throw error;

      // Update application status if needed
      if (data.application_id && feedbackData.recommendation) {
        let applicationStatus = 'interviewed';
        if (feedbackData.recommendation === 'hire') {
          applicationStatus = 'accepted';
        } else if (feedbackData.recommendation === 'reject') {
          applicationStatus = 'rejected';
        }

        await supabase
          .from('job_applications')
          .update({ 
            status: applicationStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.application_id);
      }

      return { interview: data, error: null };
    } catch (error) {
      console.error('Complete interview error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Request interview reschedule (candidate side - requires employer approval)
  rescheduleInterview: async (interviewId, newDateTime, reason = '') => {
    try {
      // Get existing interview data
      const { data: existingInterview, error: fetchError } = await supabase
        .from('interview_schedules')
        .select('scheduled_at')
        .eq('id', interviewId)
        .single();

      if (fetchError) throw fetchError;

      const newScheduledAt = new Date(newDateTime);
      
      // Create a reschedule request instead of directly updating the interview
      const { data, error } = await supabase
        .from('interview_reschedule_requests')
        .insert({
          interview_id: interviewId,
          requested_by: (await supabase.auth.getUser()).data.user.id,
          original_scheduled_at: existingInterview.scheduled_at,
          new_scheduled_at: newScheduledAt.toISOString(),
          reason: reason || null,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      // No email sent here - will be sent when employer approves/rejects
      // In-app notification is handled by database trigger

      return { rescheduleRequest: data, error: null };
    } catch (error) {
      console.error('Request interview reschedule error:', error);
      return { rescheduleRequest: null, error: error.message };
    }
  },

  // Reschedule interview (employer side - includes application update)
  rescheduleInterviewAsEmployer: async (interviewId, newDateTime, notes = '') => {
    try {
      // Get existing interview data to detect changes
      const { data: existingInterview, error: fetchError } = await supabase
        .from('interview_schedules')
        .select('scheduled_at')
        .eq('id', interviewId)
        .single();

      if (fetchError) throw fetchError;

      const scheduledAt = new Date(newDateTime);
      
      const { data, error } = await supabase
        .from('interview_schedules')
        .update({
          scheduled_at: scheduledAt.toISOString(),
          notes: notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', interviewId)
        .select()
        .single();

      if (error) throw error;

      // Update application interview_scheduled_at (only employers can do this)
      if (data.application_id) {
        await supabase
          .from('job_applications')
          .update({ 
            interview_scheduled_at: scheduledAt.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', data.application_id);
      }

      // Send email notification for rescheduling (optional - don't fail if it errors)
      try {
        const oldDate = new Date(existingInterview.scheduled_at);
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-interview-update', {
          body: {
            interviewId: interviewId,
            eventType: 'rescheduled',
            metadata: {
              oldDate: oldDate.toLocaleDateString(),
              oldTime: oldDate.toLocaleTimeString(),
              newDate: scheduledAt.toLocaleDateString(),
              newTime: scheduledAt.toLocaleTimeString()
            }
          }
        });
        
        if (emailError) {
          console.warn('Email notification failed (non-critical):', emailError);
        } else {
          console.log('Reschedule email notifications sent successfully');
        }
      } catch (emailError) {
        // Log error but don't fail the entire operation
        console.warn('Failed to send reschedule email notification (non-critical):', emailError);
      }

      return { interview: data, error: null };
    } catch (error) {
      console.error('Reschedule interview as employer error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Approve reschedule request (employer side)
  approveRescheduleRequest: async (rescheduleRequestId) => {
    try {
      // Get reschedule request details
      const { data: request, error: fetchError } = await supabase
        .from('interview_reschedule_requests')
        .select('*')
        .eq('id', rescheduleRequestId)
        .single();

      if (fetchError) throw fetchError;

      if (request.status !== 'pending') {
        throw new Error('Reschedule request is no longer pending');
      }

      const currentUserId = (await supabase.auth.getUser()).data.user.id;

      // Update reschedule request status to approved
      const { error: approveError } = await supabase
        .from('interview_reschedule_requests')
        .update({
          status: 'approved',
          approved_by: currentUserId,
          approved_at: new Date().toISOString()
        })
        .eq('id', rescheduleRequestId);

      if (approveError) throw approveError;

      // Update the actual interview schedule
      const { data: interview, error: updateError } = await supabase
        .from('interview_schedules')
        .update({
          scheduled_at: request.new_scheduled_at,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.interview_id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Update application interview_scheduled_at if applicable
      if (interview.application_id) {
        await supabase
          .from('job_applications')
          .update({ 
            interview_scheduled_at: request.new_scheduled_at,
            updated_at: new Date().toISOString()
          })
          .eq('id', interview.application_id);
      }

      // Send email notifications (optional - don't fail if it errors)
      try {
        const oldDate = new Date(request.original_scheduled_at);
        const newDate = new Date(request.new_scheduled_at);
        
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-interview-update', {
          body: {
            interviewId: request.interview_id,
            eventType: 'rescheduled',
            metadata: {
              oldDate: oldDate.toLocaleDateString(),
              oldTime: oldDate.toLocaleTimeString(),
              newDate: newDate.toLocaleDateString(),
              newTime: newDate.toLocaleTimeString(),
              approved: true
            }
          }
        });
        
        if (emailError) {
          console.warn('Email notification failed (non-critical):', emailError);
        } else {
          console.log('Reschedule approval email notifications sent successfully');
        }
      } catch (emailError) {
        console.warn('Failed to send reschedule approval email notification (non-critical):', emailError);
      }

      return { interview, error: null };
    } catch (error) {
      console.error('Approve reschedule request error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Reject reschedule request (employer side)
  rejectRescheduleRequest: async (rescheduleRequestId, rejectionReason = '') => {
    try {
      // Get reschedule request details
      const { data: request, error: fetchError } = await supabase
        .from('interview_reschedule_requests')
        .select('*')
        .eq('id', rescheduleRequestId)
        .single();

      if (fetchError) throw fetchError;

      if (request.status !== 'pending') {
        throw new Error('Reschedule request is no longer pending');
      }

      const currentUserId = (await supabase.auth.getUser()).data.user.id;

      // Update reschedule request status to rejected
      const { data, error } = await supabase
        .from('interview_reschedule_requests')
        .update({
          status: 'rejected',
          approved_by: currentUserId,
          rejected_at: new Date().toISOString(),
          rejection_reason: rejectionReason || 'No reason provided'
        })
        .eq('id', rescheduleRequestId)
        .select()
        .single();

      if (error) throw error;

      // No email needed here - in-app notification is sent by trigger
      // Could optionally send an email notification about rejection

      return { rescheduleRequest: data, error: null };
    } catch (error) {
      console.error('Reject reschedule request error:', error);
      return { rescheduleRequest: null, error: error.message };
    }
  },

  // Get pending reschedule requests for an interview
  getPendingRescheduleRequest: async (interviewId) => {
    try {
      const { data, error } = await supabase
        .from('interview_reschedule_requests')
        .select('*')
        .eq('interview_id', interviewId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      return { rescheduleRequest: data, error: null };
    } catch (error) {
      console.error('Get pending reschedule request error:', error);
      return { rescheduleRequest: null, error: error.message };
    }
  },

  // Cancel interview (candidate side)
  cancelInterview: async (interviewId, reason = '') => {
    try {
      const { data, error } = await supabase
        .from('interview_schedules')
        .update({
          status: 'cancelled',
          notes: reason ? `Cancelled: ${reason}` : 'Interview cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', interviewId)
        .select(`
          id,
          application_id,
          status
        `)
        .single();

      if (error) throw error;

      // Send email notification for cancellation (optional - don't fail if it errors)
      try {
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-interview-update', {
          body: {
            interviewId: interviewId,
            eventType: 'cancelled',
            metadata: {
              reason: reason || 'Schedule conflict'
            }
          }
        });
        
        if (emailError) {
          console.warn('Email notification failed (non-critical):', emailError);
        } else {
          console.log('Cancellation email notifications sent successfully');
        }
      } catch (emailError) {
        // Log error but don't fail the entire operation
        console.warn('Failed to send cancellation email notification (non-critical):', emailError);
      }

      return { interview: data, error: null };
    } catch (error) {
      console.error('Cancel interview error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Cancel interview (employer side - includes application update)
  cancelInterviewAsEmployer: async (interviewId, reason = '') => {
    try {
      const { data, error } = await supabase
        .from('interview_schedules')
        .update({
          status: 'cancelled',
          notes: reason ? `Cancelled: ${reason}` : 'Interview cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', interviewId)
        .select(`
          id,
          application_id,
          status
        `)
        .single();

      if (error) throw error;

      // Update application status back to shortlisted (only employers can do this)
      if (data.application_id) {
        await supabase
          .from('job_applications')
          .update({ 
            status: 'shortlisted',
            interview_scheduled_at: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.application_id);
      }

      // Send email notification for cancellation (optional - don't fail if it errors)
      try {
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-interview-update', {
          body: {
            interviewId: interviewId,
            eventType: 'cancelled',
            metadata: {
              reason: reason || 'Schedule conflict'
            }
          }
        });
        
        if (emailError) {
          console.warn('Email notification failed (non-critical):', emailError);
        } else {
          console.log('Cancellation email notifications sent successfully');
        }
      } catch (emailError) {
        // Log error but don't fail the entire operation
        console.warn('Failed to send cancellation email notification (non-critical):', emailError);
      }

      return { interview: data, error: null };
    } catch (error) {
      console.error('Cancel interview as employer error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Update interview with comprehensive data (reschedule with more options)
  updateInterview: async (interviewId, interviewData) => {
    try {
      // Get the existing interview data first to detect changes
      const { data: existingInterview, error: fetchError } = await supabase
        .from('interview_schedules')
        .select('scheduled_at, status, candidate_id, interviewer_id')
        .eq('id', interviewId)
        .single();

      if (fetchError) throw fetchError;

      // Map the interviewData format to the database format
      const updateData = {
        updated_at: new Date().toISOString()
      };

      // Handle scheduled_at from either date/time combo or scheduled_at directly
      if (interviewData.scheduled_at) {
        updateData.scheduled_at = interviewData.scheduled_at;
      } else if (interviewData.date && interviewData.time) {
        const [hours, minutes] = interviewData.time.split(':');
        const scheduledDate = new Date(`${interviewData.date}T${hours}:${minutes}:00`);
        updateData.scheduled_at = scheduledDate.toISOString();
      }

      // Handle other updates
      if (interviewData.duration) updateData.duration_minutes = interviewData.duration;
      if (interviewData.interview_type) updateData.interview_type = interviewData.interview_type;
      if (interviewData.type) updateData.interview_type = interviewData.type;
      if (interviewData.meeting_link !== undefined) updateData.meeting_link = interviewData.meeting_link;
      if (interviewData.notes !== undefined) updateData.notes = interviewData.notes;
      if (interviewData.status) updateData.status = interviewData.status;
      if (interviewData.candidate_id) updateData.candidate_id = interviewData.candidate_id;
      if (interviewData.application_id) updateData.application_id = interviewData.application_id;
      // Note: job_id is not a column in interview_schedules table
      if (interviewData.candidate_name) updateData.candidate_name = interviewData.candidate_name;
      if (interviewData.candidate_email) updateData.candidate_email = interviewData.candidate_email;

      const { data, error } = await supabase
        .from('interview_schedules')
        .update(updateData)
        .eq('id', interviewId)
        .select()
        .single();

      if (error) throw error;

      // Update application interview_scheduled_at if date changed (optional, don't fail if it errors)
      if (updateData.scheduled_at && data.application_id) {
        try {
          const { error: appError } = await supabase
            .from('job_applications')
            .update({ 
              interview_scheduled_at: updateData.scheduled_at,
              updated_at: new Date().toISOString()
            })
            .eq('id', data.application_id);
          
          if (appError) {
            console.warn('Could not update job_applications table (non-critical):', appError.message);
          }
        } catch (appUpdateError) {
          // Non-critical - application might not exist or be inaccessible due to RLS
          console.warn('Job application update failed (non-critical):', appUpdateError);
        }
      }

      // Send email notification if this was a reschedule (scheduled_at changed)
      if (updateData.scheduled_at && existingInterview.scheduled_at !== updateData.scheduled_at) {
        const oldDate = new Date(existingInterview.scheduled_at);
        const newDate = new Date(updateData.scheduled_at);
        
        // Call send-interview-update edge function for email notifications (optional)
        try {
          const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-interview-update', {
            body: {
              interviewId: interviewId,
              eventType: 'rescheduled',
              metadata: {
                oldDate: oldDate.toLocaleDateString(),
                oldTime: oldDate.toLocaleTimeString(),
                newDate: newDate.toLocaleDateString(),
                newTime: newDate.toLocaleTimeString()
              }
            }
          });
          
          if (emailError) {
            console.warn('Email notification failed (non-critical):', emailError);
          } else {
            console.log('Reschedule email notifications sent successfully');
          }
        } catch (emailError) {
          // Log error but don't fail the entire operation
          console.warn('Failed to send reschedule email notification (non-critical):', emailError);
        }
      }

      return { interview: data, error: null };
    } catch (error) {
      console.error('Update interview error:', error);
      return { interview: null, error: error.message };
    }
  },

  // Auto-expire scheduled interviews that have passed their scheduled time
  autoExpireInterviews: async (employerId) => {
    try {
      const now = new Date();
      
      // Fetch all scheduled interviews for this employer that are past their scheduled time
      const { data: expiredInterviews, error: fetchError } = await supabase
        .from('interview_schedules')
        .select('id, scheduled_at, status')
        .eq('interviewer_id', employerId)
        .eq('status', 'scheduled')
        .lt('scheduled_at', now.toISOString());

      if (fetchError) throw fetchError;

      // If no expired interviews, return early
      if (!expiredInterviews || expiredInterviews.length === 0) {
        return { expiredCount: 0, error: null };
      }

      // Update all expired interviews to 'expired' status
      const { error: updateError } = await supabase
        .from('interview_schedules')
        .update({
          status: 'expired',
          updated_at: new Date().toISOString()
        })
        .eq('interviewer_id', employerId)
        .eq('status', 'scheduled')
        .lt('scheduled_at', now.toISOString());

      if (updateError) throw updateError;

      if (process.env.NODE_ENV === 'development') {
        console.log(`[autoExpireInterviews] Expired ${expiredInterviews.length} interview(s) for employer ${employerId}`);
      }

      return { expiredCount: expiredInterviews.length, error: null };
    } catch (error) {
      console.error('Auto-expire interviews error:', error);
      return { expiredCount: 0, error: error.message };
    }
  },

  // Get interview statistics for employer
  getInterviewStats: async (employerId, dateRange = null) => {
    try {
      let query = supabase
        .from('interview_schedules')
        .select(`
          id,
          status,
          scheduled_at,
          feedback,
          application:job_applications!inner(
            job:jobs!inner(
              employer_id
            )
          )
        `)
        .eq('application.job.employer_id', employerId);

      if (dateRange) {
        query = query
          .gte('scheduled_at', dateRange.start)
          .lte('scheduled_at', dateRange.end);
      }

      const { data, error } = await query;
      if (error) throw error;

      const stats = {
        total: data.length,
        scheduled: data.filter(i => i.status === 'scheduled').length,
        completed: data.filter(i => i.status === 'completed').length,
        cancelled: data.filter(i => i.status === 'cancelled').length,
        average_rating: 0,
        completion_rate: 0
      };

      // Calculate average rating
      const ratingsData = data
        .filter(i => i.feedback && i.feedback.rating)
        .map(i => parseFloat(i.feedback.rating));
      
      if (ratingsData.length > 0) {
        stats.average_rating = ratingsData.reduce((sum, rating) => sum + rating, 0) / ratingsData.length;
      }

      // Calculate completion rate
      const totalNonCancelled = stats.total - stats.cancelled;
      if (totalNonCancelled > 0) {
        stats.completion_rate = (stats.completed / totalNonCancelled) * 100;
      }

      return { stats, error: null };
    } catch (error) {
      console.error('Get interview stats error:', error);
      return { stats: null, error: error.message };
    }
  },

  // Get upcoming interviews for today/this week
  getUpcomingInterviews: async (employerId, timeframe = 'today') => {
    try {
      const now = new Date();
      let startDate, endDate;

      switch (timeframe) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
          break;
        case 'week':
          startDate = new Date(now);
          endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now);
          endDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      }

      const { data, error } = await supabase
        .from('interview_schedules')
        .select(`
          id,
          scheduled_at,
          duration_minutes,
          interview_type,
          meeting_link,
          status,
          candidate:user_profiles!interview_schedules_candidate_id_fkey(
            full_name,
            email
          ),
          application:job_applications!inner(
            job:jobs!inner(
              id,
              title,
              employer_id
            )
          )
        `)
        .eq('application.job.employer_id', employerId)
        .eq('status', 'scheduled')
        .gte('scheduled_at', startDate.toISOString())
        .lt('scheduled_at', endDate.toISOString())
        .order('scheduled_at', { ascending: true });

      if (error) throw error;
      return { interviews: data || [], error: null };
    } catch (error) {
      console.error('Get upcoming interviews error:', error);
      return { interviews: [], error: error.message };
    }
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const utilityService = {
  // Test database connection
  testConnection: async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('count', { count: 'exact', head: true })
        .limit(1);
      
      if (error) throw error;
      return { connected: true, error: null };
    } catch (error) {
      console.error('Database connection test failed:', error);
      return { connected: false, error: error.message };
    }
  },

  // Get database health status
  getHealthStatus: async () => {
    try {
      const tests = await Promise.allSettled([
        supabase.from('user_profiles').select('count', { count: 'exact', head: true }).limit(1),
        supabase.from('jobs').select('count', { count: 'exact', head: true }).limit(1),
        supabase.from('job_applications').select('count', { count: 'exact', head: true }).limit(1)
      ]);

      const health = {
        user_profiles: tests[0].status === 'fulfilled',
        jobs: tests[1].status === 'fulfilled',
        job_applications: tests[2].status === 'fulfilled',
        overall: tests.every(test => test.status === 'fulfilled')
      };

      return { health, error: null };
    } catch (error) {
      console.error('Health check failed:', error);
      return { health: null, error: error.message };
    }
  }
};

// Export all services
export default {
  auth: authService,
  jobs: jobService,
  candidates: candidateService,
  applications: applicationService,
  messaging: messagingService,
  analytics: analyticsService,
  team: teamService,
  interviews: interviewService,
  utility: utilityService
};
